import {StreamingItem} from '@PosterWhiteboard/items/streaming-item.class';
import type {CopyableItemStyles, InitItemOpts, ItemObject, ItemSplitObjects, TimelineResizeData} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {mapNumberToRange, roundPrecision} from '@Utils/math.util';
import {RemoveVideoItemBackground} from '@PosterWhiteboard/items/video-item/remove-video-item-background.class';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {doesVideoHaveAudio, loadVideo} from '@Utils/video.util';
import type {RemoveVideoBackgroundTrimData} from '@Utils/video.util';
import {ItemEffects} from '@PosterWhiteboard/classes/item-effects.class';
import {FabricImage} from '@postermywall/fabricjs-2';
import {repoVideoURLForModel, shouldLoadGreenScreenMp4, UserVideoSizeType, UserVideoSource} from '@/libraries/user-video-library';
import {loadImageAsync} from '@Utils/image.util';
import type {RGBA} from '@Utils/color.util';
import {colorToRgbaArray} from '@Utils/color.util';
import {extractDominantColorsFromVideo, scheduleDominantColorExtraction} from '@Utils/dominant-color.util';
import type {DeepPartial} from '@/global';
import type {StickerItemObject} from '@PosterWhiteboard/items/sticker-item.class';
import {ItemCrop} from '@PosterWhiteboard/classes/item-crop.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.types';
import {DEFAULT_VIDEO_EXTENSION, isVideoLoopingEnabledForItem} from '@PosterWhiteboard/items/video-item/video-item.types';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';

export type {VideoItemObject};
export {isVideoLoopingEnabledForItem, DEFAULT_VIDEO_EXTENSION};

const VERSION_2 = 2;
const PLAY_INTERRUPTED_BY_PAUSE_ERROR_MESSAGE = 'The play() request was interrupted by a call to pause()';
const GREEN_SCREEN_COLOR_RGBA: RGBA = [42, 250, 6, 1];

export class VideoItem extends StreamingItem {
  declare _fabricObject: FabricImage;
  public gitype: ITEM_TYPE = ITEM_TYPE.VIDEO;
  public hashedFilename = '';
  public duration = 0;
  public hasTransparency = false;
  public frameRate = 0;
  public startTime = 0;
  public effects: ItemEffects;
  public endTime = 0;
  public volume = 1;
  public speed = 1;
  public override version = 2;
  public hasAudio: null | boolean = null;
  public htmlElement!: HTMLVideoElement;
  public fileExtension = DEFAULT_VIDEO_EXTENSION;
  public videoSource!: UserVideoSource;
  public removeBackground: RemoveVideoItemBackground;
  public cropData: ItemCrop;
  public timelineSpriteHashedFilename?: string;
  public timelineSpriteFrameWidth?: number;
  public timelineSpriteFrameHeight?: number;
  public timelineSpriteNumSprites?: number;
  public timelineSpriteFps?: number;
  protected currentLoadedImageFrameSrc?: string;
  private dominantColors: RGBA[] = [];
  private dominantColorsRequestId = 0;
  private isDominantColorsDisposed = false;

  public constructor(page: Page) {
    super(page);
    this.effects = new ItemEffects(this);
    this.removeBackground = new RemoveVideoItemBackground(this);
    this.cropData = new ItemCrop();
  }

  override get fabricObject(): FabricImage {
    return this._fabricObject;
  }

  public isStreamingItemPlaying(): boolean {
    return !!this.htmlElement && !this.htmlElement.paused;
  }

  public shouldPauseAtStreamEnd(): boolean {
    return false;
  }

  public hasUniformStroke(): boolean {
    return this.version >= VERSION_2;
  }

  public override onRemove(): void {
    super.onRemove();
    this.isDominantColorsDisposed = true;
    this.htmlElement.pause();
  }

  public override async onItemAddedToPage(): Promise<void> {
    await super.onItemAddedToPage();
    this.scheduleDominantColorsUpdate();
  }

  public override getDuration(): number {
    const baseDuration = this.endTime - this.startTime;
    return baseDuration / this.speed;
  }

  public override getPageEndTime(): number {
    if (isVideoLoopingEnabledForItem(this)) {
      return super.getPageEndTime();
    }
    return this.onPageStartTime + this.getDuration();
  }

  public getStreamDuration(): number {
    return this.getDuration();
  }

  // A non-looping video's on-page span derives from its source trim, so getPageEndTime
  // ignores onPageEndTime — carving the interval alone (the base behaviour) would leave
  // both halves playing the full clip. Split the trim window at the playhead instead,
  // mirroring splitAudioItem. A looping video's span is onPageEndTime-driven, so the
  // base split is correct for it.
  public override getSplitObjectsForTime(splitTime: number): ItemSplitObjects {
    if (isVideoLoopingEnabledForItem(this)) {
      return super.getSplitObjectsForTime(splitTime);
    }
    const timeIntoItem = splitTime - this.onPageStartTime;
    const trimSplitPoint = Number((this.startTime + timeIntoItem * this.speed).toFixed(2));
    // Anchor the tail to where the head's rounded trim actually ends (mirrors splitAudioItem) so
    // the two halves tile exactly — using the raw splitTime can leave a sub-frame gap or overlap.
    const headEndTime = this.onPageStartTime + (trimSplitPoint - this.startTime) / this.speed;
    return {
      firstHalf: {startTime: this.startTime, endTime: trimSplitPoint, onPageEndTime: headEndTime},
      secondHalf: {startTime: trimSplitPoint, endTime: this.endTime, onPageStartTime: headEndTime, onPageEndTime: this.getPageEndTime()},
    };
  }

  // A non-looping video's on-page span derives from the source trim, so the trim
  // window must move with the resized edge or the serialized span snaps back.
  public override applyTimelineResize(data: TimelineResizeData): void {
    const originalStartTime = this.onPageStartTime;
    const originalEndTime = this.getPageEndTime();

    if (data.handle === 'left' && data.startTime !== originalStartTime) {
      this.startTime = Number(Math.max(0, this.startTime + (data.startTime - originalStartTime) * this.speed).toFixed(2));
    }

    if (data.handle === 'right' && data.endTime !== originalEndTime) {
      this.endTime = Number(Math.min(this.duration, this.endTime + (data.endTime - originalEndTime) * this.speed).toFixed(2));
    }

    super.applyTimelineResize({...data, startTime: Math.max(0, data.startTime)});
  }

  public override clearInProgressBackgroundRemoval(): void {
    this.removeBackground.clearInProgressState();
  }

  public hasGettyContent(): boolean {
    return this.videoSource === UserVideoSource.GETTY;
  }

  public override isPremium(): boolean {
    return this.hasGettyContent();
  }

  protected override itemObjectHasDestructiveChanges(oldItemObject: VideoItemObject): boolean {
    return this.hasRemoveBackgroundChanged(oldItemObject);
  }

  protected override onItemDoubleClicked(): void {
    if (this.isBackground) {
      getPosterUi().onOpenVideoBackgroundCropModal();
      return;
    }
    getPosterUi().onOpenVideoCropModal(this);
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      border: this.border.toObject(),
      textBorder: this.border.toObject(),
      effects: this.effects.toObject(),
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<StickerItemObject> = {};
    if (copiedStyles.effects !== undefined) {
      stylesObj.effects = copiedStyles.effects;
    }

    if (copiedStyles.rx !== undefined) {
      stylesObj.effects = {
        ...stylesObj.effects,
        edgeType: this.effects.edgeType,
        edgeThickness: this.effects.edgeThickness,
      };
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.effects = {
        ...stylesObj.effects,
        tint: this.effects.tint,
        tintColor: this.effects.tintColor,
      };
    }

    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.textBorder);
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY']);
    await this.updateFromObject({...commonStylesObj, ...stylesObj});
  }

  private hasRemoveBackgroundChanged(oldValues: VideoItemObject): boolean {
    return this.removeBackground.isBackgroundRemoved !== oldValues.removeBackground.isBackgroundRemoved;
  }

  public override toObject(): VideoItemObject {
    return {
      ...super.toObject(),
      effects: this.effects.toObject(),
      hashedFilename: this.hashedFilename,
      duration: this.duration,
      hasTransparency: this.hasTransparency,
      frameRate: this.frameRate,
      startTime: this.startTime,
      endTime: this.endTime,
      speed: this.speed,
      fileExtension: this.fileExtension,
      videoSource: this.videoSource,
      removeBackground: this.removeBackground.toObject(),
      cropData: this.cropData.toObject(),
      volume: this.htmlElement.volume,
      timelineSpriteHashedFilename: this.timelineSpriteHashedFilename,
      timelineSpriteFrameWidth: this.timelineSpriteFrameWidth,
      timelineSpriteFrameHeight: this.timelineSpriteFrameHeight,
      timelineSpriteNumSprites: this.timelineSpriteNumSprites,
      timelineSpriteFps: this.timelineSpriteFps,
    };
  }

  public async stopItemStream(): Promise<void> {
    if (this.htmlElement && roundPrecision(this.htmlElement.currentTime, 1) !== roundPrecision(this.startTime, 1)) {
      await this.stop();
    }
  }

  public override async stop(): Promise<void> {
    await this.pause();
    this.htmlElement.currentTime = this.startTime;
  }

  public override getColors(): RGBA[] {
    const colors: RGBA[] = super.getColors();

    if (this.fabricObject.shadow?.isGlow()) {
      colors.push(colorToRgbaArray(this.fabricObject.shadow.color));
    }

    if (this.effects.tint) {
      colors.push(this.effects.tintColor);
    }

    if (this.effects.multiply) {
      colors.push(this.effects.multiplyColor);
    }

    colors.push(...this.dominantColors);

    return colors;
  }

  public override copyVals(obj: DeepPartial<VideoItemObject>): void {
    const {effects, removeBackground, cropData, ...itemObj} = obj;
    super.copyVals(itemObj);
    this.effects.copyVals(effects);
    this.removeBackground.copyVals(removeBackground);
    this.cropData.copyVals(cropData);
  }

  public override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    if (this.cropData?.cropped) {
      this.applyCropToFabricObject();
      if (this.isBackground) {
        this.scaleFabricObjectToFillPage();
      }
    } else {
      this.resetFabricObjectCropPosition();
    }
    this.updateRemoveColorFilter();
    this.effects.applyItemEffects();
    await this.effects.applyBorderEffects();
    this.htmlElement.muted = !this.hasAudio || this.volume === 0;
  }

  public override async pause(): Promise<void> {
    await super.pause();
    return new Promise((resolve) => {
      this.htmlElement.pause();
      resolve();
    });
  }

  protected override async fixChanges(applyVersionFixesData: Record<string, any>): Promise<void> {
    await super.fixChanges(applyVersionFixesData);
    this.applyFixForVersion2();
    this.fixBrightness();
  }

  private applyFixForVersion2(): void {
    if (!this.hasUniformStroke()) {
      this.border.solidBorderThickness = Math.round(this.border.solidBorderThickness * this.fabricObject.scaleX);
      this.version = VERSION_2;
    }
  }

  public getCurrentTime(): number {
    const timeWithinVideoSegment = this.htmlElement.currentTime - this.startTime;
    return timeWithinVideoSegment / this.speed;
  }

  public async seekToPageTime(): Promise<void> {
    await this.seek(this.page.clock.getCurrentTime());
    this.page.fabricCanvas.requestRenderAll();
  }

  public override seek(time: number): Promise<void> {
    const target = time * this.speed + this.startTime;
    // No-op writes don't fire 'seeked' in all browsers — short-circuit so the
    // promise can't hang and leave SyncItemToPageClock locked.
    if (Math.abs(this.htmlElement.currentTime - target) < 0.001) {
      this.page.fabricCanvas.requestRenderAll();
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.htmlElement.addEventListener(
        'seeked',
        () => {
          this.page.fabricCanvas.requestRenderAll();
          resolve();
        },
        {
          capture: false,
          once: true,
        }
      );
      this.htmlElement.currentTime = target;
    });
  }

  public override async play(): Promise<void> {
    if (!this.htmlElement) {
      throw new Error('Video element is not defined');
    }

    try {
      const promise = this.htmlElement.play();

      if (typeof promise !== 'undefined') {
        await promise;
      }
    } catch (e) {
      if (!(e instanceof Error && e.message.includes(PLAY_INTERRUPTED_BY_PAUSE_ERROR_MESSAGE))) {
        throw e;
      }
    }
  }

  public getFrameTimeForPageTime(pageTime: number): number {
    const duration = this.getDuration();
    // The video's local time base is pageTime - onPageStartTime — a bg video
    // slotted mid-timeline starts its media at 0 when the page clock reaches
    // its slot. Looping items wrap; non-looping (bg) videos hold the last frame.
    const itemTime = Math.max(0, pageTime - this.onPageStartTime);
    const timeWithinStream = isVideoLoopingEnabledForItem(this) ? (itemTime > duration ? itemTime % duration : itemTime) : Math.min(itemTime, duration);
    return timeWithinStream * this.speed + this.startTime + 0.001;
  }

  protected getVideoUrl(): string {
    if (this.page.poster.mode.isWebpage()) {
      return repoVideoURLForModel(this, UserVideoSizeType.SCREEN);
    }
    return repoVideoURLForModel(this);
  }

  protected override async updateFabricObjectFromObject(obj: DeepPartial<VideoItemObject>): Promise<void> {
    await super.updateFabricObjectFromObject(obj);
    if (obj.volume !== undefined) {
      this.htmlElement.volume = obj.volume;
    }
    if (obj.speed !== undefined) {
      this.speed = obj.speed;
      if (this.htmlElement) {
        this.htmlElement.playbackRate = obj.speed;
      }
    }
  }

  // Background videos don't loop — their on-page span equals one playback pass.
  // Changing the speed resizes that span; if the page clock now sits outside
  // the new span, the currently-rendered frame would belong to no bg. Call this
  // from the user-facing speed control (NOT from updateFromObject — history
  // restores, snapshots, version fixes etc. all pass `speed` through that path
  // and shouldn't jump the scrubber). Non-bg videos loop, so their bounds don't
  // shrink and this is a no-op for them.
  public async reseekIfClockOutsideBgBounds(): Promise<void> {
    if (!this.isBackground) {
      return;
    }
    const currentTime = this.page.clock.getCurrentTime();
    const bgStart = this.onPageStartTime;
    const bgEnd = this.getPageEndTime();
    if (currentTime >= bgStart && currentTime < bgEnd) {
      return;
    }
    await this.page.seek(bgStart);
  }

  public isMuted(): boolean {
    return this.volume === 0;
  }

  public toggleMuteUnmuteState(): void {
    const newVolume = this.volume === 0 ? 1 : 0;
    void this.updateFromObject({volume: newVolume});
  }

  protected isGreenScreenMp4Loaded(): boolean {
    return shouldLoadGreenScreenMp4(this.fileExtension, this.isItemTransparent());
  }

  public isItemTransparent(): boolean {
    return this.removeBackground.isBackgroundRemoved || this.hasTransparency;
  }

  public getStartTimeForNonRemovedBgVideo(): number {
    if (this.removeBackground.isBackgroundRemoved) {
      return this.startTime + (this.removeBackground.trimData.startTime ?? 0);
    }
    return this.startTime;
  }

  public getEndTimeForNonRemovedBgVideo(): number {
    if (this.removeBackground.isBackgroundRemoved) {
      return this.endTime + (this.removeBackground.trimData.startTime ?? 0);
    }
    return this.endTime;
  }

  /**
   * Brightness used to have a range of -100 to 100 in HTML5 posterbuilder build using fabricJs (Version: 1.5),
   * After upgrading to fabricJs (version: 2.0), new range for brightness filter is -0.4 to 0.4 hence with the upgraded
   * posterBuilder we need to map the old brightness value of images.
   */
  private fixBrightness(): void {
    if (this.page.poster.version < POSTER_VERSION.FABRIC_2_UPDATE) {
      this.effects.brightness = mapNumberToRange(this.effects.brightness, -100, 100, -0.4, 0.4);
    }
  }

  public async enableRemoveBackgroundFlag(trimData: RemoveVideoBackgroundTrimData): Promise<void> {
    await this.updateFromObject({
      startTime: 0,
      endTime: trimData.trimEnd - trimData.trimStart,
      removeBackground: {
        isBackgroundRemoved: true,
        trimData: {
          startTime: trimData.trimStart,
          endTime: trimData.trimEnd,
        },
      },
    });
  }

  public async disableRemoveBackgroundFlag(): Promise<void> {
    await this.updateFromObject({
      startTime: this.removeBackground.trimData.startTime,
      endTime: this.removeBackground.trimData.endTime,
      removeBackground: {
        isBackgroundRemoved: false,
      },
    });
  }

  public async onItemTrimmed(): Promise<void> {
    await this.seekToPageTime();
  }

  protected resetFabricObjectCropPosition(): void {
    if (!this.fabricObject) {
      return;
    }

    this.fabricObject.set({
      cropX: 0,
      cropY: 0,
    });
  }

  protected applyCropToFabricObject(): void {
    if (!this.fabricObject) {
      return;
    }

    const scaledCropData = this.cropData.getScaledCropData(this.htmlElement);
    this.fabricObject.set({
      cropX: scaledCropData.x,
      cropY: scaledCropData.y,
      width: scaledCropData.width,
      height: scaledCropData.height,
    });

    this.fabricObject.setCoords();
  }

  protected override async getFabricObjectForItem(opts?: InitItemOpts): Promise<FabricImage> {
    await this.loadVideoElement();

    let scaleCalculationOpts = opts;
    if (this.cropData?.cropped) {
      const {width: croppedWidth, height: croppedHeight} = scaleCalculationOpts ?? {};
      const {width, height} = this.getUncroppedDimensions(croppedWidth, croppedHeight);

      scaleCalculationOpts = {
        ...opts,
        width,
        height,
      };
    }

    return new FabricImage(this.htmlElement, {
      ...super.getCommonOptions(),
      ...this.getScalesAndShadowToMaintainScaledDimensions(this.htmlElement.width, this.htmlElement.height, scaleCalculationOpts),
      strokeUniform: true,
    });
  }

  private getUncroppedDimensions(croppedItemWidth?: number, croppedItemHeight?: number): {width: number; height: number} {
    if (croppedItemWidth && croppedItemHeight && this.cropData.imageWidth && this.cropData.width && this.cropData.imageHeight && this.cropData.height) {
      return {
        width: (croppedItemWidth * this.cropData.imageWidth) / this.cropData.width,
        height: (croppedItemHeight * this.cropData.imageHeight) / this.cropData.height,
      };
    }

    return {
      width: this.htmlElement.width,
      height: this.htmlElement.height,
    };
  }

  public async setImageFrame(repoTempFilePath: string): Promise<void> {
    const src = `${window.PMW.BASE_URL}repo/temp/${repoTempFilePath}`;
    if (this.currentLoadedImageFrameSrc === src) {
      return;
    }

    const image = await loadImageAsync(src);
    let finalImage = image;
    if (this.cropData.cropped) {
      finalImage = await this.cropData.applyCropToImage(image);
    }

    const scaledWidth = this.fabricObject.scaleX * this.fabricObject.width;
    const scaledHeight = this.fabricObject.scaleY * this.fabricObject.height;

    this.fabricObject.setElement(finalImage);
    this.fabricObject.set({
      scaleX: scaledWidth / finalImage.width,
      scaleY: scaledHeight / finalImage.height,
      ...(this.cropData.cropped && {cropX: 0, cropY: 0}),
    });
  }

  protected override async onItemUpdatedFromObject(): Promise<void> {
    this.removeBackground.resumeBackgroundRemovalIfNeeded();
    return Promise.resolve();
  }

  protected async loadVideoElement(): Promise<void> {
    await this.removeBackground.ensureRemovedBackgroundVideoExistsIfNeeded();
    this.htmlElement = await loadVideo(this.getVideoUrl(), {videoElementAttributes: this.getVideoElementAttributes()});
    this.hasAudio = doesVideoHaveAudio(this.htmlElement);
    // SyncItemToPageClock drives looping by seeking back to startTime whenever the
    // page-clock tick says we've crossed the trim boundary. Native HTML5 loop
    // would only restart at source duration (past our trim) and fight that, so
    // we keep it off and let the page clock be the single source of truth.
    this.htmlElement.loop = false;
    this.htmlElement.muted = !this.hasAudio || this.volume === 0;
    this.htmlElement.playbackRate = this.speed;
    this.duration = this.htmlElement.duration;
    this.endTime = this.endTime === 0 ? this.htmlElement.duration : this.endTime;

    this.htmlElement.width = this.htmlElement.videoWidth;
    this.htmlElement.height = this.htmlElement.videoHeight;
  }

  protected getVideoElementAttributes(): Record<string, any> {
    return {};
  }

  private updateRemoveColorFilter(): void {
    if (this.isGreenScreenMp4Loaded()) {
      this.effects.removeColor = true;
      this.effects.removeColorValue = GREEN_SCREEN_COLOR_RGBA;
    } else {
      this.effects.removeColor = false;
    }
  }

  private scheduleDominantColorsUpdate(): void {
    if (this.page.poster.mode.isGeneration() || this.isDominantColorsDisposed || !this.hashedFilename || !this.htmlElement) {
      return;
    }
    this.dominantColorsRequestId += 1;
    const requestId = this.dominantColorsRequestId;
    scheduleDominantColorExtraction(() => {
      if (this.isDominantColorsDisposed || this.dominantColorsRequestId !== requestId || !this.htmlElement) {
        return;
      }
      const colors = extractDominantColorsFromVideo(this.htmlElement);
      if (colors.length === 0) {
        return;
      }
      this.dominantColors = colors;
      this.page.poster.fire('colors:extracted');
    });
  }
}
